import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, AlertCircle, Info, X } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  usePublicAnnouncement,
  type AnnouncementSeverity,
} from '@/services/system-announcement.service'

/**
 * Banner global de aviso/manutenção exibido no topo de toda a página.
 *
 * Faz polling do endpoint público `/api/system/announcement` a cada 60s.
 * Quando `active=true` e `message` não-vazio, renderiza uma faixa
 * `position: fixed` no topo da viewport com a cor do severity:
 *
 *   info     → azul              (informativo neutro)
 *   warning  → amarelo/laranja    (atenção, mudança planejada)
 *   critical → laranja brand FCamara #f97e00 pulsante (manutenção / downtime)
 *
 * Layout (e a parte importante): banner é `fixed` (sempre visível),
 * MAS aplica `padding-top` ao <body> via efeito + ResizeObserver. Isso
 * empurra TUDO da página pra baixo da altura real do banner, evitando
 * que ele cubra o Header/menu. Quando admin desliga ou apaga a mensagem,
 * o padding volta a zero — sem fantasma de espaço.
 *
 * Dismiss: botão X fecha o banner em MEMÓRIA (não persiste). Refresh
 * traz de volta — comportamento intencional pra avisos de manutenção
 * que devem ser vistos a cada nova sessão. Se admin atualizar a
 * mensagem (poll detecta novo `updatedAt`), o aviso reaparece mesmo
 * sem refresh.
 */
const SEVERITY_STYLES: Record<
  AnnouncementSeverity,
  { bg: string; border: string; icon: typeof AlertTriangle; pulse: boolean }
> = {
  info: {
    bg: 'bg-gradient-to-r from-sky-600 via-sky-500 to-sky-600',
    border: 'border-sky-400/40',
    icon: Info,
    pulse: false,
  },
  warning: {
    bg: 'bg-gradient-to-r from-amber-600 via-amber-500 to-amber-600',
    border: 'border-amber-400/40',
    icon: AlertTriangle,
    pulse: false,
  },
  critical: {
    // Laranja brand FCamara (#f97e00) com pontas levemente escurecidas
    // pra dar profundidade. Mantém pulse no ícone pra chamar atenção.
    bg: 'bg-gradient-to-r from-[#e06d00] via-[#f97e00] to-[#e06d00]',
    border: 'border-[#f97e00]/60',
    icon: AlertCircle,
    pulse: true,
  },
}

export function MaintenanceBanner() {
  const { data } = usePublicAnnouncement()
  const bannerRef = useRef<HTMLDivElement>(null)
  const [measuredHeight, setMeasuredHeight] = useState(0)

  // Dismiss em memória — guarda o `updatedAt` do aviso fechado pelo
  // user. Refresh re-mounta o componente, esse estado vira null e o
  // banner reaparece (comportamento pedido). Se admin atualizar a
  // mensagem (poll detecta novo updatedAt), o aviso é "novo" e
  // reaparece mesmo na mesma sessão.
  const [dismissedAt, setDismissedAt] = useState<string | null>(null)
  const isDismissed = dismissedAt !== null && data?.updatedAt === dismissedAt

  const isVisible = !!(data?.active && data.message.trim().length > 0) && !isDismissed
  const severity = (data?.severity ?? 'info') as AnnouncementSeverity
  const style = SEVERITY_STYLES[severity] ?? SEVERITY_STYLES.info
  const Icon = style.icon

  // Mede a altura real do banner e aplica como padding-top no <body>
  // pra empurrar o conteúdo. Usa ResizeObserver pra responder a quebra
  // de linha do texto + mudança de breakpoint (sm: py-3 → base: py-4).
  useEffect(() => {
    if (!isVisible) {
      document.body.style.paddingTop = ''
      document.documentElement.style.removeProperty('--maintenance-banner-height')
      setMeasuredHeight(0)
      return
    }

    const update = () => {
      const h = bannerRef.current?.offsetHeight ?? 72
      setMeasuredHeight(h)
      document.body.style.paddingTop = `${h}px`
      // CSS variable consumida por elementos sticky (ex: Header em
      // `top: var(--maintenance-banner-height, 0px)`) pra colarem
      // embaixo do banner ao scrollar em vez de atrás dele.
      document.documentElement.style.setProperty('--maintenance-banner-height', `${h}px`)
    }

    update()
    const ro = new ResizeObserver(update)
    if (bannerRef.current) ro.observe(bannerRef.current)
    window.addEventListener('resize', update)

    return () => {
      ro.disconnect()
      window.removeEventListener('resize', update)
      document.body.style.paddingTop = ''
      document.documentElement.style.removeProperty('--maintenance-banner-height')
    }
  }, [isVisible, data?.message, data?.severity])

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          ref={bannerRef}
          initial={{ y: -80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -80, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 280, damping: 28 }}
          className={`fixed top-0 inset-x-0 z-[60] ${style.bg} border-b ${style.border} shadow-lg shadow-black/30`}
          role="alert"
          aria-live="polite"
          data-measured-height={measuredHeight}
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3">
            <div className="flex-shrink-0">
              <Icon
                className={`w-5 h-5 sm:w-6 sm:h-6 text-white drop-shadow ${
                  style.pulse ? 'animate-pulse' : ''
                }`}
                aria-hidden
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm sm:text-base font-medium text-white leading-snug tracking-tight">
                {data.message}
              </p>
            </div>
            <button
              type="button"
              onClick={() => setDismissedAt(data.updatedAt)}
              className="flex-shrink-0 -m-1.5 p-1.5 rounded-md text-white/80 hover:text-white hover:bg-white/15 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
              aria-label="Fechar aviso"
              title="Fechar (refresh traz de volta)"
            >
              <X className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/**
 * Hook utilitário pra components que precisam saber se o banner está
 * visível (ex: ajustar `top` de um header sticky próprio). Hoje só
 * exporta um booleano — a altura efetiva já vem aplicada via padding
 * do body pelo `MaintenanceBanner`.
 */
export function useMaintenanceBannerVisible(): boolean {
  const { data } = usePublicAnnouncement()
  return !!(data?.active && data.message.trim().length > 0)
}
